import type { ResourceType } from "@medplum/fhirtypes";
import {
  ACTIVITY_DEFINITION_COLLECTION_NAME,
  ALLERGY_INTOLERANCE_COLLECTION_NAME,
  APPOINTMENT_COLLECTION_NAME,
  AVAILABILITY_BLOCK_COLLECTION_NAME,
  BASIC_COLLECTION_NAME,
  BOOKED_SLOT_COLLECTION_NAME,
  CARE_PLAN_COLLECTION_NAME,
  CLINICAL_IMPRESSION_COLLECTION_NAME,
  COMMUNICATION_COLLECTION_NAME,
  CONDITION_COLLECTION_NAME,
  DEVICE_COLLECTION_NAME,
  DEVICE_DEFINITION_COLLECTION_NAME,
  DOCUMENT_REFERENCE_COLLECTION_NAME,
  ENCOUNTER_COLLECTION_NAME,
  EPISODE_OF_CARE_COLLECTION_NAME,
  FLAG_COLLECTION_NAME,
  GOAL_COLLECTION_NAME,
  LOCATION_COLLECTION_NAME,
  MEDICATION_STATEMENT_COLLECTION_NAME,
  OBSERVATION_COLLECTION_NAME,
  PATIENT_COLLECTION_NAME,
  PLAN_DEFINITION_COLLECTION_NAME,
  PRACTITIONER_COLLECTION_NAME,
  PROCEDURE_COLLECTION_NAME,
  QUESTIONNAIRE_COLLECTION_NAME,
  QUESTIONNAIRE_RESPONSE_COLLECTION_NAME,
  RELATED_PERSON_COLLECTION_NAME,
  REQUEST_GROUP_COLLECTION_NAME,
  SCHEDULE_COLLECTION_NAME,
  SERVICE_REQUEST_COLLECTION_NAME,
  SLOT_COLLECTION_NAME,
  TASK_COLLECTION_NAME,
} from "./constants";

/** Predicate deciding whether a FHIR resource type is readable for the current session. */
export type CanRead = (resourceType: ResourceType) => boolean;

/**
 * The FHIR resource type each data-store collection syncs and subscribes to.
 *
 * This is the single source of truth for scoping a narrow AccessPolicy: every
 * collection is always registered, but only fetches from the server and opens a
 * subscription when its resource type is readable. Keyed by the collection's
 * registry name. Includes `basic`, which is registered but intentionally absent
 * from `FhirCollectionName`. The three Slot-backed collections (`slot`,
 * `booked-slot`, `availability-block`) all map to `Slot`, so a single `Slot`
 * grant unlocks them together.
 */
export const COLLECTION_RESOURCE_TYPE: Record<string, ResourceType> = {
  findingDefinition: "ObservationDefinition",
  [ACTIVITY_DEFINITION_COLLECTION_NAME]: "ActivityDefinition",
  [ALLERGY_INTOLERANCE_COLLECTION_NAME]: "AllergyIntolerance",
  [APPOINTMENT_COLLECTION_NAME]: "Appointment",
  [AVAILABILITY_BLOCK_COLLECTION_NAME]: "Slot",
  [BASIC_COLLECTION_NAME]: "Basic",
  [BOOKED_SLOT_COLLECTION_NAME]: "Slot",
  [CARE_PLAN_COLLECTION_NAME]: "CarePlan",
  [CLINICAL_IMPRESSION_COLLECTION_NAME]: "ClinicalImpression",
  [COMMUNICATION_COLLECTION_NAME]: "Communication",
  [CONDITION_COLLECTION_NAME]: "Condition",
  [DEVICE_COLLECTION_NAME]: "Device",
  [DEVICE_DEFINITION_COLLECTION_NAME]: "DeviceDefinition",
  [DOCUMENT_REFERENCE_COLLECTION_NAME]: "DocumentReference",
  [ENCOUNTER_COLLECTION_NAME]: "Encounter",
  [EPISODE_OF_CARE_COLLECTION_NAME]: "EpisodeOfCare",
  [FLAG_COLLECTION_NAME]: "Flag",
  [GOAL_COLLECTION_NAME]: "Goal",
  [LOCATION_COLLECTION_NAME]: "Location",
  [MEDICATION_STATEMENT_COLLECTION_NAME]: "MedicationStatement",
  [OBSERVATION_COLLECTION_NAME]: "Observation",
  [PATIENT_COLLECTION_NAME]: "Patient",
  [PLAN_DEFINITION_COLLECTION_NAME]: "PlanDefinition",
  [PRACTITIONER_COLLECTION_NAME]: "Practitioner",
  [PROCEDURE_COLLECTION_NAME]: "Procedure",
  [QUESTIONNAIRE_COLLECTION_NAME]: "Questionnaire",
  [QUESTIONNAIRE_RESPONSE_COLLECTION_NAME]: "QuestionnaireResponse",
  [RELATED_PERSON_COLLECTION_NAME]: "RelatedPerson",
  [REQUEST_GROUP_COLLECTION_NAME]: "RequestGroup",
  [SCHEDULE_COLLECTION_NAME]: "Schedule",
  [SERVICE_REQUEST_COLLECTION_NAME]: "ServiceRequest",
  [SLOT_COLLECTION_NAME]: "Slot",
  [TASK_COLLECTION_NAME]: "Task",
};

/**
 * True when a collection may fetch/subscribe under the given read predicate.
 *
 * With no predicate the collection is always allowed — this is the provider-app
 * default, where the session can read everything the registry syncs. A collection
 * whose resource type is unknown fails closed under a predicate: we can't prove
 * it's readable, so we don't risk the 403.
 */
export function isCollectionReadable(collectionName: string, canRead?: CanRead): boolean {
  if (!canRead) return true;
  const resourceType = COLLECTION_RESOURCE_TYPE[collectionName];
  return resourceType !== undefined && canRead(resourceType);
}

/** The collection registry names that may sync/subscribe under the given read predicate. */
export function selectReadableCollectionNames(canRead?: CanRead): string[] {
  return Object.keys(COLLECTION_RESOURCE_TYPE).filter((name) => isCollectionReadable(name, canRead));
}
